import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionPortalFlow, SubscriptionType } from "schemas/dashboard";
import { DisplayName, Plans } from "schemas/payments";

import { TERMS_AND_CONDITIONS_LINK_YEARLY } from "constants/influencer";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

interface ManageDialogProps {
  open: boolean;
  onClose: () => void;
  openCancel: () => void;
}

export default function ManageDialog({
  open,
  onClose,
  openCancel,
}: ManageDialogProps) {
  const { handleManagePlan, checkoutLoading, handleCheckout, cancelLoading } =
    useSubscription();
  const { subscription, plans, isStarterPlan, plan } =
    useContext(SubscriptionContext);
  const activeEmailVolumes = subscription?.emailVolumes?.filter(
    (ev) => ev.status === SubscriptionType.ACTIVE,
  );

  const isStarterOnProPlan =
    isStarterPlan && (plan === Plans.PRO || plan === Plans.PRO_YEARLY);

  const getUpdatePlan = () => {
    if (plan === Plans.PRO) {
      return Plans.PRO_YEARLY;
    } else {
      return Plans.VA_YEARLY;
    }
  };

  const trackTermsAndCondition = () => {
    trackEvent("Terms and Conditions for VA Plan Clicked");
  };

  if (!plan) return <></>;

  return (
    <Dialog maxWidth="sm" open={open} onClose={onClose}>
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
        }}
        onClick={onClose}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogTitle>Manage Current Plan</DialogTitle>
      <DialogContent>
        {plan && (
          <Typography paragraph>
            You are currently on the <strong>{DisplayName[plan]}</strong> plan
            {isStarterOnProPlan ? " (we moved you from Starter to Pro)" : ""}.
            Use the link below to change your current plan, modify your payment
            method, and see invoice history.
          </Typography>
        )}

        <LoadingButton
          loading={checkoutLoading === plan}
          variant="outlined"
          onClick={() => handleManagePlan(plan)}
        >
          Manage Plan
        </LoadingButton>

        {plan === Plans.PRO && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" component="div">
              Get Annual Discount{" "}
              <Box component="span" sx={{ color: "info.main" }}>
                (${plans?.[Plans.PRO_YEARLY]?.price}/month)
              </Box>
            </Typography>
            <Typography paragraph>
              By switching to yearly, you will save <strong>25%</strong> on your
              monthly payment. Click on the button below to switch to an annual
              subscription.
            </Typography>
            <LoadingButton
              variant="contained"
              onClick={() => {
                handleCheckout(getUpdatePlan());
              }}
              disableElevation
              loading={checkoutLoading === getUpdatePlan()}
            >
              Switch
            </LoadingButton>
          </>
        )}
        {plan === Plans.VA && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" component="div">
              Get Annual Discount{" "}
              <Box component="span" sx={{ color: "info.main" }}>
                (${plans?.[Plans.VA_YEARLY]?.price}/month)
              </Box>
            </Typography>
            <Typography paragraph>
              By switching to yearly, you will save <strong>25%</strong> on your
              monthly payment. Click on the button below to switch to an annual
              subscription.
            </Typography>
            <Typography paragraph>
              <a
                href={TERMS_AND_CONDITIONS_LINK_YEARLY}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackTermsAndCondition}
              >
                Terms & Conditions Apply
              </a>
            </Typography>
            <LoadingButton
              variant="contained"
              onClick={() => {
                handleCheckout(getUpdatePlan());
              }}
              disableElevation
              loading={checkoutLoading === Plans.VA_YEARLY}
            >
              Upgrade to Yearly
            </LoadingButton>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" paragraph>
          Cancel Subscription
        </Typography>
        {plan && (
          <Typography paragraph>
            By ending your subscription, you will lose access to your{" "}
            <strong>{DisplayName[plan]}</strong> plan benefits.
          </Typography>
        )}

        {isStarterOnProPlan && (
          <Alert severity="warning" sx={{ my: 2 }}>
            We currently charge you the same rate as the previous{" "}
            <strong>Starter </strong> tier. If you cancel, the discount will
            also be cancelled.
          </Alert>
        )}

        {plans &&
          subscription?.subscription?.discountedPrice &&
          subscription?.subscription?.status === SubscriptionType.ACTIVE &&
          plan in plans &&
          subscription.subscription.discountedPrice <=
            plans[plan as Plans].price && (
            <Alert severity="warning" sx={{ my: 2 }}>
              You're currently paying the previous {DisplayName[plan]} rate of{" "}
              <strong>
                ${subscription.subscription.discountedPrice}/month
              </strong>{" "}
              (the standard price is now{" "}
              <strong>${plans?.[plan as Plans].price}/month</strong>). If you
              cancel your subscription, you'll lose this lower rate, and any
              future subscription will be charged at the new price.
            </Alert>
          )}

        {activeEmailVolumes && activeEmailVolumes.length > 0 && (
          <Alert severity="warning" sx={{ my: 2 }}>
            All your email volume add-on packages will also be{" "}
            <strong>cancelled effective immediately</strong>. If you want to
            renew your plan in future, you will need to repurchase the email
            volume packages separately.
          </Alert>
        )}

        <LoadingButton
          variant="outlined"
          color="secondary"
          loading={cancelLoading}
          onClick={() => {
            if (
              plan &&
              [
                Plans.VA,
                Plans.VA_YEARLY,
                Plans.VA_QUARTERLY,
                Plans.PRO,
                Plans.PRO_YEARLY,
                Plans.PRO_QUARTERLY,
              ].includes(plan as Plans)
            ) {
              openCancel();
            } else {
              handleManagePlan(
                plan,
                SubscriptionPortalFlow.subscription_cancel,
              );
            }
          }}
        >
          Cancel Subscription
        </LoadingButton>
      </DialogContent>
    </Dialog>
  );
}
